/**
 * GlobalSearchResults — resultados da busca global de mensagens do chat.
 *
 * Componente puramente apresentacional: recebe as hits já resolvidas pelo
 * ChatHome (via `searchMessages`) e as agrupa por conversa.
 *
 * - Agrupa `hits` por `conversation_id`, preservando a ordem de chegada
 *   (a facade já devolve DESC por data).
 * - Para cada grupo exibe o título da conversa (resolvido pelo `resolveTitle`
 *   que o ChatHome monta a partir do cache ['conversations']).
 * - Cada linha mostra o snippet com o termo casado envolto em <mark> e uma
 *   marca de tempo curta/relativa (mesmo estilo de ChatListRow).
 * - Clique na linha chama `onOpen(conversation_id, id)`.
 * - Estado vazio quando não há hits para uma query não vazia.
 *
 * UX: o conteúdo rola no próprio container (vertical); texto quebra em linha,
 * nunca gera scroll horizontal.
 */
import { Box, Stack, Text, UnstyledButton } from '@mantine/core'
import { IconSearchOff } from '@tabler/icons-react'
import type { SearchHit, Snippet } from '@/lib/messageSearch'
import { EmptyState } from '@/components/ui/EmptyState'

interface Props {
  hits: SearchHit[]
  query: string
  /** Resolve o título da conversa a partir do id (cache ['conversations']). */
  resolveTitle: (conversationId: number) => string
  onOpen: (conversationId: number, messageId: number) => void
}

/** Marca de tempo curta/relativa — mesma lógica de ChatListRow.timeAgo. */
function timeAgo(iso: string): string {
  if (!iso) return ''
  const diffSec = Math.floor((Date.now() - new Date(iso).getTime()) / 1000)
  if (diffSec < 60) return 'agora'
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)}min`
  if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h`
  return `${Math.floor(diffSec / 86400)}d`
}

/** Renderiza o snippet com o trecho casado envolto em <mark>. */
function renderSnippet(snippet: Snippet) {
  const { text, matchStart, matchLen } = snippet
  if (matchStart < 0 || matchLen <= 0) {
    return <>{text}</>
  }
  const before = text.slice(0, matchStart)
  const match = text.slice(matchStart, matchStart + matchLen)
  const after = text.slice(matchStart + matchLen)
  return (
    <>
      {before}
      <mark>{match}</mark>
      {after}
    </>
  )
}

interface Group {
  conversationId: number
  hits: SearchHit[]
}

function groupByConversation(hits: SearchHit[]): Group[] {
  const order: number[] = []
  const map = new Map<number, SearchHit[]>()
  for (const h of hits) {
    if (!map.has(h.conversation_id)) {
      map.set(h.conversation_id, [])
      order.push(h.conversation_id)
    }
    map.get(h.conversation_id)!.push(h)
  }
  return order.map((cid) => ({ conversationId: cid, hits: map.get(cid)! }))
}

export default function GlobalSearchResults({ hits, query, resolveTitle, onOpen }: Props) {
  if (hits.length === 0) {
    return (
      <Box style={{ flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden' }}>
        <EmptyState
          icon={IconSearchOff}
          title="Nenhuma mensagem encontrada"
          description={`Nenhum resultado para “${query}”.`}
          size="md"
        />
      </Box>
    )
  }

  const groups = groupByConversation(hits)

  return (
    <Box style={{ flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden' }}>
      <Stack gap={0}>
        {groups.map((g) => (
          <Box key={g.conversationId}>
            {/* Cabeçalho do grupo: título da conversa.
                NÃO usar position:sticky — múltiplos grupos com top:0 empilham
                seus cabeçalhos no mesmo ponto e se sobrepõem no scroll. */}
            <Text
              size="xs"
              fw={700}
              c="dimmed"
              tt="uppercase"
              px="md"
              py={6}
              style={{
                background: 'var(--mantine-color-body)',
                borderBottom: '1px solid var(--ai-border-light, var(--mantine-color-gray-2))',
              }}
            >
              {resolveTitle(g.conversationId)}
            </Text>

            {g.hits.map((h) => (
              <UnstyledButton
                key={h.id}
                onClick={() => onOpen(h.conversation_id, h.id)}
                px="md"
                py="sm"
                style={{
                  display: 'block',
                  width: '100%',
                  borderBottom:
                    '1px solid var(--ai-border-light, var(--mantine-color-gray-1))',
                  transition: 'background 0.15s',
                }}
              >
                <Text
                  size="sm"
                  style={{
                    overflowWrap: 'anywhere',
                    wordBreak: 'break-word',
                    whiteSpace: 'normal',
                  }}
                >
                  {renderSnippet(h.snippet)}
                </Text>
                <Text size="xs" c="dimmed" mt={2}>
                  {timeAgo(h.created_at)}
                </Text>
              </UnstyledButton>
            ))}
          </Box>
        ))}
      </Stack>
    </Box>
  )
}
